/**
 * Home Screen
 *
 * Responsibilities:
 *   - Render the saved lists (name + item count), sorted by updatedAt desc.
 *   - Floating "+" button navigates to the new-list Editor.
 *   - Resume banner shows when an incomplete session exists; tap resumes,
 *     X hides the banner (session preserved).
 *   - Tap-to-run branching:
 *       (a) no active session anywhere  -> startSession + navigate to runner
 *       (b) active session on same list -> navigate to runner (preserve state)
 *       (c) active session on different list -> confirm dialog; confirm
 *           discards the other session and starts a new one
 *   - Long-press reveals an action menu with Edit and Delete.
 *   - Overflow menu contains Export (clipboard + textarea fallback).
 */

import {
  getAllLists,
  getListById,
  deleteList,
  exportListsAsJson,
  importListsFromJson
} from '../lists.js';
import {
  getSession,
  startSession,
  clearSession,
  hasActiveSessionForList,
  hasAnyActiveSession
} from '../session.js';
import { longPress } from '../ui/long-press.js';
import {
  showResumeModal,
  showConfirmDialog,
  showImportDialog
} from '../ui/modal-stubs.js';

const WRITE_FAIL_MSG = "Couldn't save — progress may not survive close";
const COPY_FAIL_MSG = 'Could not copy to clipboard — copy manually below';
const IMPORT_INVALID_MSG = "That doesn't look like a valid export";

function escapeHtml(s) {
  return String(s)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

export function renderHome(root, _params, api) {
  const state = { resumeDismissed: false, overflowOpen: false, exportFallbackJson: null };
  doRender();

  function doRender() {
    const allLists = getAllLists();
    const session = getSession();
    const showResume = session && !session.completed && !state.resumeDismissed;

    root.innerHTML = `
      <main class="screen screen-home">
        <header class="home-header">
          <div class="home-title">
            <img src="/icons/icon-192.png" alt="" class="home-title__icon" aria-hidden="true">
            <h1 tabindex="-1">Checklists</h1>
          </div>
          <button type="button" class="home-overflow" data-action="overflow"
                  aria-label="More options"
                  aria-expanded="${state.overflowOpen ? 'true' : 'false'}">
            ⋯
          </button>
        </header>

        ${state.overflowOpen ? `
          <div class="home-overflow-menu" role="menu">
            <button type="button" role="menuitem" data-action="export">Export lists</button>
            <button type="button" role="menuitem" data-action="import">Import lists</button>
          </div>
        ` : ''}

        ${showResume ? `
          <div class="home-resume-banner" role="status">
            <button type="button" class="home-resume-banner__tap"
                    data-action="resume"
                    aria-label="Resume ${escapeHtml(session.listName)}">
              Resume ${escapeHtml(session.listName)} — ${session.currentIndex} of ${session.items.length}
            </button>
            <button type="button" class="home-resume-banner__dismiss"
                    data-action="dismiss-resume"
                    aria-label="Dismiss resume banner">×</button>
          </div>
        ` : ''}

        ${allLists.length === 0 ? `
          <div class="home-empty">
            <p>No lists yet.</p>
            <p class="muted">Tap + to create your first list.</p>
          </div>
        ` : `
          <ul class="home-list" aria-label="Saved lists">
            ${allLists.map(l => `
              <li class="home-list-row" data-list-id="${escapeHtml(l.id)}">
                <button type="button" class="home-list-row__tap"
                        data-action="run" data-list-id="${escapeHtml(l.id)}">
                  <span class="home-list-row__name">${escapeHtml(l.name)}</span>
                  <span class="home-list-row__count muted">${l.items.length} item${l.items.length === 1 ? '' : 's'}</span>
                </button>
              </li>
            `).join('')}
          </ul>
        `}

        <button type="button" class="home-new" data-action="new-list"
                aria-label="New list">+</button>

        ${state.exportFallbackJson !== null ? `
          <div class="home-export-fallback">
            <p class="muted">Copy the JSON below:</p>
            <textarea readonly aria-label="Exported lists JSON">${escapeHtml(state.exportFallbackJson)}</textarea>
            <button type="button" data-action="close-export-fallback">Close</button>
          </div>
        ` : ''}
      </main>
    `;

    wireEvents();
  }

  function wireEvents() {
    root.querySelector('[data-action="new-list"]').addEventListener('click', () => {
      api.navigateTo('editor', {});
    });

    const overflowBtn = root.querySelector('[data-action="overflow"]');
    overflowBtn.addEventListener('click', () => {
      const willOpen = !state.overflowOpen;
      state.overflowOpen = willOpen;
      doRender();
      if (willOpen) {
        // Close overflow on outside click. Listener is attached to document
        // after the menu opens and removed when the menu closes.
        const outsideHandler = (e) => {
          if (!e.target.closest('.home-overflow-menu') &&
              !e.target.closest('[data-action="overflow"]')) {
            state.overflowOpen = false;
            document.removeEventListener('click', outsideHandler, true);
            doRender();
          }
        };
        // Defer attachment to the next tick so the opening click itself
        // does not immediately close the menu.
        setTimeout(() => document.addEventListener('click', outsideHandler, true), 0);
      }
    });

    const exportBtn = root.querySelector('[data-action="export"]');
    if (exportBtn) {
      exportBtn.addEventListener('click', () => {
        state.overflowOpen = false;
        handleExport();
      });
    }

    const importBtn = root.querySelector('[data-action="import"]');
    if (importBtn) {
      importBtn.addEventListener('click', () => {
        state.overflowOpen = false;
        doRender();
        handleImport();
      });
    }

    const resumeBtn = root.querySelector('[data-action="resume"]');
    if (resumeBtn) {
      resumeBtn.addEventListener('click', () => {
        const active = getSession();
        if (!active) { doRender(); return; }
        showResumeModal({
          listName: active.listName,
          currentIndex: active.currentIndex,
          total: active.items.length,
          onResume: () => api.navigateTo('runner', {}),
          onStartOver: () => {
            clearSession();
            doRender();
          }
        });
      });
    }
    const dismissResumeBtn = root.querySelector('[data-action="dismiss-resume"]');
    if (dismissResumeBtn) {
      dismissResumeBtn.addEventListener('click', () => {
        state.resumeDismissed = true;
        doRender();
      });
    }

    for (const row of root.querySelectorAll('.home-list-row')) {
      const listId = row.getAttribute('data-list-id');
      const tapBtn = row.querySelector('[data-action="run"]');
      tapBtn.addEventListener('click', () => handleRun(listId));
      longPress(row, () => openActionMenu(listId));
    }

    const closeFallback = root.querySelector('[data-action="close-export-fallback"]');
    if (closeFallback) {
      closeFallback.addEventListener('click', () => {
        state.exportFallbackJson = null;
        doRender();
      });
    }
  }

  function handleRun(listId) {
    const list = getListById(listId);
    if (!list) return;

    if (hasActiveSessionForList(listId)) {
      api.navigateTo('runner', {});
      return;
    }

    if (hasAnyActiveSession()) {
      const active = getSession();
      showConfirmDialog({
        message: `A run is in progress on "${active.listName}". Discard it and start "${list.name}"?`,
        confirmLabel: 'Discard and start',
        cancelLabel: 'Cancel',
        onConfirm: () => {
          const { writeOk } = startSession(list);
          if (!writeOk) api.showError(WRITE_FAIL_MSG);
          api.navigateTo('runner', {});
        }
      });
      return;
    }

    const { writeOk } = startSession(list);
    if (!writeOk) api.showError(WRITE_FAIL_MSG);
    api.navigateTo('runner', {});
  }

  function openActionMenu(listId) {
    const list = getListById(listId);
    if (!list) return;

    const existing = document.querySelector('.home-action-menu');
    if (existing) existing.remove();

    const menu = document.createElement('div');
    menu.className = 'home-action-menu';
    menu.setAttribute('role', 'menu');
    menu.setAttribute('aria-label', `Actions for ${list.name}`);
    menu.innerHTML = `
      <button type="button" role="menuitem" data-action="edit">Edit</button>
      <button type="button" role="menuitem" data-action="delete">Delete</button>
      <button type="button" role="menuitem" data-action="cancel">Cancel</button>
    `;

    const close = () => menu.remove();

    menu.querySelector('[data-action="edit"]').addEventListener('click', () => {
      close();
      if (hasActiveSessionForList(listId)) {
        showConfirmDialog({
          message: 'A run is in progress. Discard the run to continue?',
          confirmLabel: 'Discard run',
          cancelLabel: 'Cancel',
          onConfirm: () => {
            clearSession();
            api.navigateTo('editor', { listId });
          }
        });
      } else {
        api.navigateTo('editor', { listId });
      }
    });

    menu.querySelector('[data-action="delete"]').addEventListener('click', () => {
      close();
      showConfirmDialog({
        message: `Delete "${list.name}"? This can't be undone.`,
        confirmLabel: 'Delete',
        cancelLabel: 'Cancel',
        onConfirm: () => {
          deleteList(listId);
          doRender();
        }
      });
    });

    menu.querySelector('[data-action="cancel"]').addEventListener('click', close);

    document.body.appendChild(menu);
  }

  function handleImport() {
    showImportDialog({
      onConfirm: (json) => {
        let result;
        try {
          result = importListsFromJson(json);
        } catch (_e) {
          api.showError(IMPORT_INVALID_MSG);
          return;
        }
        if (!result.writeOk) {
          api.showError(WRITE_FAIL_MSG);
        }
        const { imported, updated, skipped } = result;
        if (imported === 0 && updated === 0) {
          api.showToast(skipped > 0
            ? `No new lists to import (${skipped} skipped)`
            : 'No lists to import');
        } else {
          const parts = [];
          if (imported) parts.push(`${imported} imported`);
          if (updated) parts.push(`${updated} updated`);
          if (skipped) parts.push(`${skipped} skipped`);
          api.showToast(`Import complete — ${parts.join(', ')}`);
        }
        doRender();
      }
    });
  }

  async function handleExport() {
    const json = exportListsAsJson();
    const clipboard = navigator.clipboard;
    if (clipboard && typeof clipboard.writeText === 'function') {
      try {
        await clipboard.writeText(json);
        api.showToast('Lists copied to clipboard');
        return;
      } catch (_e) {
        // Fall through to textarea fallback.
      }
    }
    api.showError(COPY_FAIL_MSG);
    state.exportFallbackJson = json;
    doRender();
  }
}

export { showResumeModal };
